/**
 * live-refetch.ts
 *
 * Framework-free refetch trigger shared by every live-updating case view (landlord
 * `useCaseActivityRefresh`, tenant case page): a trailing-debounced `onChange`, plus the two
 * "events may have been missed" edges — the realtime subscription re-establishing after a drop
 * (false -> true after the first subscription) and the tab going hidden -> visible.
 */
const DEFAULT_DEBOUNCE_MS = 300;
export function createLiveRefetch(opts) {
    const { onChange, debounceMs = DEFAULT_DEBOUNCE_MS } = opts;
    let timer = null;
    let everSubscribed = false;
    let wasSubscribed = false;
    let lastVisibility = null;
    function trigger() {
        if (timer)
            clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            onChange();
        }, debounceMs);
    }
    return {
        trigger,
        onSubscribedChange(isSubscribed) {
            if (isSubscribed && !wasSubscribed && everSubscribed)
                trigger();
            if (isSubscribed)
                everSubscribed = true;
            wasSubscribed = isSubscribed;
        },
        onVisibilityChange(state) {
            // Anything may have changed while the tab was hidden. Callers forward `visibilitychange`
            // events, so a 'visible' that does not follow a 'visible' is a return to the tab.
            if (state === 'visible' && lastVisibility !== 'visible')
                trigger();
            lastVisibility = state;
        },
        dispose() {
            if (timer)
                clearTimeout(timer);
            timer = null;
        },
    };
}
/**
 * Forward the document's `visibilitychange` events to `liveRefetch`. The returned cleanup removes
 * the listener AND disposes `liveRefetch`, so a hook's effect teardown is just that cleanup.
 */
export function bindLiveRefetchToDocument(liveRefetch, doc) {
    const onVisibility = () => liveRefetch.onVisibilityChange(doc.visibilityState === 'visible' ? 'visible' : 'hidden');
    doc.addEventListener('visibilitychange', onVisibility);
    return () => {
        doc.removeEventListener('visibilitychange', onVisibility);
        liveRefetch.dispose();
    };
}
//# sourceMappingURL=live-refetch.js.map